import { useEffect, useState } from "react";
import { Play, Plus, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/States";

const fmt = (h) => `${Math.floor(h)}h ${Math.round((h - Math.floor(h)) * 60).toString().padStart(2, "0")}m`;

export function Elapsed({ start }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  const s = Math.max(0, Math.floor((Date.now() - new Date(start).getTime()) / 1000));
  return <span className="font-mono tabular-nums" data-testid="clock-elapsed">{String(Math.floor(s / 3600)).padStart(2, "0")}:{String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:{String(s % 60).padStart(2, "0")}</span>;
}

export default function ClockCard({ projects, onChange }) {
  const status = useFetch("/time/status");
  const [project, setProject] = useState("");
  const [note, setNote] = useState("");
  const [manual, setManual] = useState(false);
  const [m, setM] = useState({ date: new Date().toISOString().slice(0, 10), hours: "" });
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); status.reload(); onChange?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const open = status.data?.open;
  return (
    <section className="rounded-2xl border bg-card p-6" data-testid="clock-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div><p className="eyebrow">Clock</p><p className="stat mt-1 text-2xl">{open ? <>On the job · <Elapsed start={open.start} /></> : "Off the clock"}</p>{open && <p className="text-sm text-muted-foreground" data-testid="clock-project">{open.project_name}{open.note ? ` · ${open.note}` : ""}</p>}</div>
        <div className="text-right text-sm text-muted-foreground"><p>This week</p><p className="stat text-2xl text-foreground" data-testid="week-hours">{fmt(status.data?.week_hours || 0)}</p></div>
      </div>
      {open ? (
        <Button size="lg" className="mt-5 rounded-full" onClick={() => act(() => api.post("/time/clock-out"), "Clocked out")} disabled={busy} data-testid="clock-out-button"><Square className="mr-2 h-4 w-4" />Clock out</Button>
      ) : (
        <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <select value={project} onChange={(e) => setProject(e.target.value)} className="h-10 rounded-md border bg-background px-3 text-sm" aria-label="Project" data-testid="clock-project-select">
            <option value="">Pick a project…</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Input placeholder="What are you working on? (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="clock-note-input" />
          <div className="flex gap-2">
            <Button className="rounded-full" disabled={!project || busy} onClick={() => act(() => api.post("/time/clock-in", { project_id: project, note }), "Clocked in")} data-testid="clock-in-button"><Play className="mr-2 h-4 w-4" />Clock in</Button>
            <Button variant="outline" className="rounded-full" onClick={() => setManual(!manual)} data-testid="manual-toggle"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      {manual && !open && (
        <form className="mt-4 grid gap-3 rounded-xl border bg-background p-4 sm:grid-cols-[1fr_1fr_auto]" onSubmit={(e) => { e.preventDefault(); act(() => api.post("/time/entries", { project_id: project, date: m.date, hours: Number(m.hours), note }), "Hours added").then(() => setM({ ...m, hours: "" })); }} data-testid="manual-form">
          <div className="space-y-1"><Label htmlFor="m-date">Date</Label><Input id="m-date" type="date" required value={m.date} onChange={(e) => setM({ ...m, date: e.target.value })} data-testid="manual-date-input" /></div>
          <div className="space-y-1"><Label htmlFor="m-hours">Hours</Label><Input id="m-hours" type="number" step="0.25" min="0.25" max="24" required value={m.hours} onChange={(e) => setM({ ...m, hours: e.target.value })} data-testid="manual-hours-input" /></div>
          <Button type="submit" className="self-end rounded-full" disabled={!project || busy} data-testid="manual-submit">{busy ? <Spinner /> : "Add hours"}</Button>
        </form>
      )}
    </section>
  );
}

export function EntryList({ entries, canDelete, onDelete }) {
  if (!entries.length) return <p className="text-sm text-muted-foreground" data-testid="entries-empty">No hours logged in this range.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border">
      <table className="w-full text-sm" data-testid="entries-table">
        <thead className="bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Person</th><th className="px-3 py-2">Project</th><th className="px-3 py-2 text-right">Hours</th><th className="px-3 py-2 text-right">Cost</th><th className="px-3 py-2">Note</th><th /></tr></thead>
        <tbody className="divide-y">
          {entries.map((e) => (
            <tr key={e.id} data-testid={`entry-${e.id}`}>
              <td className="px-3 py-2 font-mono text-xs">{e.date}</td><td className="px-3 py-2">{e.user_name}</td><td className="px-3 py-2">{e.project_name}</td>
              <td className="px-3 py-2 text-right font-mono">{e.hours}</td><td className="px-3 py-2 text-right font-mono text-muted-foreground">{e.rate ? `$${e.cost.toFixed(2)}` : "–"}</td>
              <td className="max-w-[200px] truncate px-3 py-2 text-muted-foreground">{e.note}{e.qbo_id && <span className="ml-2 rounded bg-emerald-100 px-1.5 text-[10px] text-emerald-800">QB</span>}</td>
              <td className="px-2 py-2">{canDelete(e) && <button type="button" onClick={() => onDelete(e)} aria-label="Delete entry" className="text-muted-foreground hover:text-destructive" data-testid={`entry-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
